import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import reviewApi from "../api/reviewApi";
import ModulePageShell from "../components/common/ModulePageShell";
import LoadingState from "../components/common/LoadingState";
import EmptyState from "../components/common/EmptyState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";

const ReviewDetailPage = () => {
  const { reviewId } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const [showGuide, setShowGuide] = React.useState(false);
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadReviews = can(PERFORMANCE_PERMISSIONS.REVIEWS_READ);
  const canUpdateReviews = can(PERFORMANCE_PERMISSIONS.REVIEWS_UPDATE);
  const canFinalizeReviews = can(PERFORMANCE_PERMISSIONS.REVIEWS_FINALIZE);
  const canApproveRecommendations = can(PERFORMANCE_PERMISSIONS.REVIEW_APPROVE);

  if (!canReadReviews) {
    return (
      <ModulePageShell
        title="Review Detail"
        description="Process submit, acknowledge, finalize, increment, and promotion workflows."
      >
        <EmptyState message="You do not have permission to view review details." />
      </ModulePageShell>
    );
  }

  const reviewQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.reviewDetail, reviewId],
    queryFn: () => reviewApi.getReviewById(reviewId),
    enabled: Boolean(reviewId),
  });

  const refreshReview = () =>
    queryClient.invalidateQueries({
      queryKey: [PERFORMANCE_QUERY_KEYS.reviewDetail, reviewId],
    });

  const submitMutation = useMutation({
    mutationFn: () => reviewApi.submitReview(reviewId),
    onSuccess: refreshReview,
  });
  const acknowledgeMutation = useMutation({
    mutationFn: (payload) => reviewApi.acknowledgeReview(reviewId, payload),
    onSuccess: refreshReview,
  });
  const finalizeMutation = useMutation({
    mutationFn: () => reviewApi.finalizeReview(reviewId),
    onSuccess: refreshReview,
  });
  const incrementMutation = useMutation({
    mutationFn: (payload) => reviewApi.approveIncrement(reviewId, payload),
    onSuccess: refreshReview,
  });
  const promotionMutation = useMutation({
    mutationFn: (payload) => reviewApi.approvePromotion(reviewId, payload),
    onSuccess: refreshReview,
  });

  const { register, handleSubmit } = useForm({
    defaultValues: { remarks: "" },
  });
  const { register: registerIncrement, handleSubmit: handleIncrementSubmit } =
    useForm({
      defaultValues: {
        recommended: true,
        percentage: 0,
        amount: 0,
        currency: "INR",
        remarks: "",
      },
    });
  const { register: registerPromotion, handleSubmit: handlePromotionSubmit } =
    useForm({
      defaultValues: { recommended: true, designation: "", remarks: "" },
    });

  const showActionToast = (title) =>
    toast({ title, status: "success", duration: 2000 });

  const review = reviewQuery.data?.data;

  return (
    <ModulePageShell
      title="Review Detail"
      description="Process submit, acknowledge, finalize, increment, and promotion workflows."
      onRefresh={() => reviewQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {reviewQuery.isLoading ? (
        <LoadingState />
      ) : !review ? (
        <EmptyState message="Review not found." />
      ) : (
        <VStack align="stretch" spacing={4}>
          <Box
            bg={bg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
          >
            <Text fontWeight="bold">Employee Self Review</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={3}>
              <Text>
                <strong>Employee:</strong>{" "}
                {review.employeeId?.name || review.employeeId || "-"}
              </Text>
              <Text>
                <strong>Manager:</strong>{" "}
                {review.managerId?.name || review.managerId || "-"}
              </Text>
              <Text>
                <strong>Goals:</strong> {review.goalScore ?? 0}
              </Text>
              <Text>
                <strong>Competency:</strong> {review.competencyScore ?? 0}
              </Text>
              <Text>
                <strong>Overall:</strong> {review.overallRating ?? 0}
              </Text>
              <Text>
                <strong>Status:</strong> {review.finalStatus || "-"}
              </Text>
            </SimpleGrid>
          </Box>

          <Box
            bg={bg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
          >
            <Text fontWeight="bold" mb={3}>
              Review Workflow
            </Text>
            <HStack>
              <Button
                onClick={async () => {
                  await submitMutation.mutateAsync();
                  showActionToast("Review submitted");
                }}
                isLoading={submitMutation.isPending}
                isDisabled={!canUpdateReviews}
              >
                Submit
              </Button>
              <Button
                onClick={async () => {
                  await finalizeMutation.mutateAsync();
                  showActionToast("Review finalized");
                }}
                isLoading={finalizeMutation.isPending}
                isDisabled={!canFinalizeReviews}
              >
                Finalize
              </Button>
            </HStack>
            <Box
              as="form"
              mt={3}
              onSubmit={handleSubmit(async (values) => {
                await acknowledgeMutation.mutateAsync(values);
                showActionToast("Review acknowledged");
              })}
            >
              <HStack>
                <FormControl>
                  <FormLabel>Employee Acknowledgement Remarks</FormLabel>
                  <Input {...register("remarks")} />
                </FormControl>
                <Button
                  mt={8}
                  type="submit"
                  isLoading={acknowledgeMutation.isPending}
                  isDisabled={!canUpdateReviews}
                >
                  Acknowledge
                </Button>
              </HStack>
            </Box>
          </Box>

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
            <Box
              bg={bg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
            >
              <Text fontWeight="bold" mb={3}>
                Increment Approval
              </Text>
              <form
                onSubmit={handleIncrementSubmit(async (values) => {
                  await incrementMutation.mutateAsync({
                    ...values,
                    recommended: true,
                    percentage: Number(values.percentage) || 0,
                    amount: Number(values.amount) || 0,
                  });
                  showActionToast("Increment approved");
                })}
              >
                <VStack align="stretch" spacing={2}>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Percentage"
                    {...registerIncrement("percentage")}
                  />
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Amount"
                    {...registerIncrement("amount")}
                  />
                  <Input
                    placeholder="Currency"
                    {...registerIncrement("currency")}
                  />
                  <Input
                    placeholder="Remarks"
                    {...registerIncrement("remarks")}
                  />
                  <Button
                    type="submit"
                    isLoading={incrementMutation.isPending}
                    isDisabled={!canApproveRecommendations}
                  >
                    Approve Increment
                  </Button>
                </VStack>
              </form>
            </Box>

            <Box
              bg={bg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
            >
              <Text fontWeight="bold" mb={3}>
                Promotion Approval
              </Text>
              <form
                onSubmit={handlePromotionSubmit(async (values) => {
                  await promotionMutation.mutateAsync({
                    ...values,
                    recommended: true,
                  });
                  showActionToast("Promotion approved");
                })}
              >
                <VStack align="stretch" spacing={2}>
                  <Input
                    placeholder="Designation"
                    {...registerPromotion("designation")}
                  />
                  <Input
                    placeholder="Remarks"
                    {...registerPromotion("remarks")}
                  />
                  <Button
                    type="submit"
                    isLoading={promotionMutation.isPending}
                    isDisabled={!canApproveRecommendations}
                  >
                    Approve Promotion
                  </Button>
                </VStack>
              </form>
            </Box>
          </SimpleGrid>
        </VStack>
      )}
    </ModulePageShell>
  );
};

export default ReviewDetailPage;
